<template>
    <div class="relative sm:flex sm:flex-col sm:items-center">
        <LayoutToggle
            v-if="pricing.showLayouts"
            :pricing="pricing"
            :finalLayout="finalLayout"
            @changeLayout="$emit('changeLayout', $event)"
        />
        <!-- STANDARD LAYOUT -->
        <div
            v-if="pricing.headerLayout === PRICING_HEADER_LAYOUTS.standard"
            :class="[pricing.imgPosition === 'top' ? '' : 'md:flex-row']"
            class="max-w-5xl w-full flex flex-col justify-center items-center"
        >
            <div
                v-if="renderPricing.img"
                :class="[pricing.imgPosition === 'top' ? 'mb-8' : 'mb-8 md:mb-0 md:mr-8']"
                class="flex-shrink-0 block text-right"
            >
                <img
                    :style="{
                        height: `${pricing.imgSize}px`,
                        'border-radius': `${pricing.imgRadius}px`,
                    }"
                    class="inline-block rounded-md"
                    :src="renderPricing.img"
                />
            </div>

            <div
                :class="[
                    renderPricing.img && pricing.imgPosition === 'side'
                        ? 'md:text-left items-center md:items-start'
                        : 'justify-center items-center',
                ]"
                class="flex flex-col text-center"
            >
                <cUtilsEdit @edit="goToHeaderField('pricingTitle')">
                    <MarkdownHtml
                        class="leading-none tracking-tight font-bold sm:text-center"
                        :style="{
                            color: pricing.titleColor,
                            'font-size': pricing.titleSize + 'px',
                        }"
                        :markdown="renderPricing.title"
                    />
                </cUtilsEdit>
                <div v-if="areSocials" class="mt-6">
                    <SocialsList :pricing="pricing" />
                </div>
                <div class="mt-8" v-if="renderPricing.subtitle">
                    <cUtilsEdit @edit="goToHeaderField('pricingSubtitle')">
                        <MarkdownHtml
                            :style="{
                                color: pricing.titleColor,
                                'font-size': pricing.subtitleSize + 'px',
                            }"
                            class="leading-normal text-left opacity-80"
                            :markdown="renderPricing.subtitle"
                        />
                    </cUtilsEdit>
                </div>
            </div>
        </div>
        <!-- BUSINESS CARD LAYOUT WITH BACKGROUND -->
        <div v-else class="w-full px-6 flex flex-col sm:flex-row -mt-10 sm:space-x-6">
            <div
                class="w-24 sm:w-40 h-24 sm:h-40 flex-shrink-0 border-4 shadow-lg rounded-full overflow-hidden"
                :style="{
                    'border-color': renderPricing.pricingColor,
                    'background-color': renderPricing.pricingColor,
                }"
            >
                <img
                    v-if="renderPricing.img"
                    class="w-full h-full object-cover"
                    :src="renderPricing.img"
                />
                <div
                    class="w-full h-full"
                    v-else
                    :style="{ background: 'linear-gradient(180deg, #A5C0EE 0%, #FBC5EC 100%)' }"
                ></div>
            </div>
            <div class="flex-grow space-y-1 pt-6 sm:pt-16">
                <cUtilsEdit @edit="goToHeaderField('pricingTitle')">
                    <MarkdownHtml
                        class="text-3xl font-semibold"
                        :style="{
                            color: pricing.titleColor,
                        }"
                        :markdown="renderPricing.title"
                    />
                </cUtilsEdit>
                <cUtilsEdit @edit="goToHeaderField('pricingSubtitle')">
                    <MarkdownHtml
                        :style="{
                            color: pricing.titleColor,
                        }"
                        class="text-base opacity-80"
                        :markdown="renderPricing.subtitle"
                    />
                </cUtilsEdit>
            </div>
            <div v-if="areSocials" class="flex-shrink-0 pt-6 sm:pt-16">
                <SocialsList :pricing="pricing" />
            </div>
        </div>
        <div
            v-if="categoriesList.length > 1"
            :style="{ 'background-color': pricing.borderColor }"
            class="relative self-center mt-6 bg-gray-100 rounded-lg p-0.5 flex flex-col sm:flex-row sm:mt-8"
        >
            <button
                v-for="(category, index) in categoriesList"
                :key="category.id"
                @click="currentCategoryIndex = index"
                type="button"
                class="relative w-full rounded-md shadow-sm py-2 text-sm font-medium text-gray-900 whitespace-nowrap focus:outline-none sm:w-auto sm:px-8"
                :style="
                    currentCategoryIndex === index
                        ? {
                              color: renderPricing.headingsColor,
                              'background-color': renderPricing.backColor,
                              'border-color': renderPricing.borderColor,
                          }
                        : {
                              color: renderPricing.textColor,
                              'background-color': renderPricing.borderColor,
                              'border-color': 'transparent',
                          }
                "
            >
                <cUtilsEdit @edit="goToCategoryField(category.id, 'categoryName')">
                    <span>{{ category.name }}</span>
                </cUtilsEdit>
            </button>
        </div>
    </div>
</template>

<script>
import SocialsList from "./SocialsList.vue";
import LayoutToggle from "./LayoutToggle.vue";
import { MarkdownHtml } from "@easy/ui/text";
import { isObjectNotEmpty } from "@/shared/utils/helpers";
import { PRICING_HEADER_LAYOUTS } from "@/apps/pricing/services/pricing.service";
import { usePricingGoTo } from "@/views/tools/pricing/utils/usePricingGoTo";
import cUtilsEdit from "@/views/shared/cUtilsEdit.vue";

export default {
    props: {
        pricing: Object,
        renderPricing: Object,
        modelValue: Number,
        finalLayout: String,
    },
    setup() {
        const { goToHeaderField, goToCategoryField } = usePricingGoTo();
        return {
            goToHeaderField,
            goToCategoryField,
        };
    },
    components: {
        SocialsList,
        LayoutToggle,
        cUtilsEdit,
        MarkdownHtml,
    },
    emits: ["update:modelValue", "changeLayout"],
    data() {
        return {
            PRICING_HEADER_LAYOUTS,
        };
    },
    computed: {
        categoriesList() {
            return this.pricing.categories?.filter((category) => !category._deleted) || [];
        },
        areSocials() {
            return isObjectNotEmpty(this.pricing.socials);
        },
        currentCategoryIndex: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit("update:modelValue", value);
            },
        },
    },
};
</script>
